import { Button, Snackbar, TextField, Tooltip } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import RefreshIcon from '@material-ui/icons/Refresh'
import MuiAlert from '@material-ui/lab/Alert'
import CropFreeIcon from '@material-ui/icons/CropFree'
import RoomIcon from '@material-ui/icons/Room'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { getClubs } from '../../../../api/club'
import { altaEnLote } from '../../../../api/gpsdevice'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog'
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from '../../../../_metronic/_partials/controls'
import {
	deleteGpsDevice,
	getGpsDevicesStatus,
	updateGpsDevice,
} from '../../../../api/gpsdevice'
import { FRONT_URL } from '../../../../utils'
import { alertError, alertSuccess } from '../../../../utils/logger'
import Table, {
	booleanFormatter,
	buttonsStyle,
	imageFormatter,
} from '../../../components/tables/table'
import { Dialog, DialogContent, DialogTitle, IconButton } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { QRCodeCanvas } from 'qrcode.react'
import SmsCommandDialog from '../../../components/dialogs/SmsCommandDialog'
import SmsIcon from '@material-ui/icons/Sms'
import HojaPegatinasDialog from '../../../components/dialogs/HojaPegatinasDialog'
import LocalPrintshopIcon from '@material-ui/icons/LocalPrintshop'

/** Carga/desconocido ≠ "no cargando" (evita mostrar ✗ cuando isCharging es undefined) */
function chargingFormatter(val) {
	if (val === true)
		return booleanFormatter(true)
	if (val === false)
		return booleanFormatter(false)
	return <span className="text-muted" title="Sin datos recientes (dispositivo desconectado o sin paquete 0x13)">—</span>
}

function formatLastTime(val) {
	if (val == null)
		return '—'
	try {
		const d = new Date(val)
		if (Number.isNaN(d.getTime()))
			return String(val)
		return d.toLocaleString()
	} catch {
		return String(val)
	}
}

/** Antigüedad relativa con el instante exacto en tooltip. */
function ageFormatter(ageSec, row) {
	if (ageSec == null) {
		/*
		 * «Nunca» a secas mandaba a buscar por donde no era. Un aparato sin
		 * posiciones que SÍ ha hecho login habla con el servidor: la SIM, el
		 * APN y el servidor están bien, y lo que le falta es cielo. Si no ha
		 * hecho login nunca, es al revés. Se veían igual y se arreglan al revés.
		 */
		if (row?.lastLoginAt)
			return (
				<Tooltip
					title={`Conecta al servidor (última vez ${formatLastTime(row.lastLoginAt)}) pero no ha dado ninguna posición: sácalo al aire libre y reinícialo`}
				>
					<span className="text-warning">conecta, sin fix</span>
				</Tooltip>
			)
		return (
			<Tooltip title="No ha llegado nunca al servidor: revisar SIM, APN y el servidor configurado en el aparato">
				<span className="text-muted">nunca</span>
			</Tooltip>
		)
	}
	const txt =
		ageSec < 90 ? `hace ${ageSec} s`
			: ageSec < 3600 ? `hace ${Math.round(ageSec / 60)} min`
				: ageSec < 172800 ? `hace ${Math.round(ageSec / 3600)} h`
					: `hace ${Math.round(ageSec / 86400)} días`
	const cls = ageSec < 90 ? '' : ageSec < 300 ? 'text-warning' : 'text-danger'
	return (
		<Tooltip title={formatLastTime(row?.lastTimestamp)}>
			<span className={cls}>{txt}</span>
		</Tooltip>
	)
}

const STATUS_UI = {
	ok: { color: '#1BC5BD', label: 'OK' },
	warn: { color: '#FFA800', label: 'Atención' },
	critical: { color: '#F64E60', label: 'Problema' },
}

/** Punto de semáforo con los motivos en tooltip. */
function statusFormatter(status, row) {
	const ui = STATUS_UI[status] || STATUS_UI.critical
	const reasons = row?.reasons?.length ? row.reasons.join(' · ') : ui.label
	return (
		<Tooltip title={reasons}>
			<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
				<span style={{ width: 10, height: 10, borderRadius: '50%', background: ui.color, display: 'inline-block' }} />
				{ui.label}
			</span>
		</Tooltip>
	)
}

function connectedFormatter(val) {
	return val
		? <span style={{ color: '#1BC5BD', fontWeight: 600 }}>✓</span>
		: <span className="text-muted">—</span>
}

/** Estado de la SIM en la red móvil (API 1NCE), con operador en tooltip. */
function networkFormatter(net) {
	if (!net || !net.status)
		return <span className="text-muted">—</span>
	const online = net.status === 'ONLINE'
	return (
		<Tooltip title={`${net.operator || ''}${net.lastSeen ? ` · última red: ${net.lastSeen}` : ''}`}>
			<span style={{ color: online ? '#1BC5BD' : '#F64E60', fontWeight: 600, whiteSpace: 'nowrap' }}>
				{online ? '● red OK' : '● sin red'}
			</span>
		</Tooltip>
	)
}

/** MB restantes del Lifetime Flat de la SIM. */
function dataQuotaFormatter(net) {
	if (!net || net.dataRemainingMb == null)
		return <span className="text-muted">—</span>
	const pct = net.dataTotalMb ? (net.dataRemainingMb / net.dataTotalMb) * 100 : 100
	const cls = pct < 5 ? 'text-danger' : pct < 15 ? 'text-warning' : ''
	return <span className={cls} style={{ whiteSpace: 'nowrap' }}>{net.dataRemainingMb} MB</span>
}

/**
 * De quien es cada aparato.
 *
 * El semaforo devuelve el club como id, no como nombre: se pide la lista una
 * vez y se traduce aqui. Sin esta columna no hay forma de saber que hay en
 * existencias y que esta entregado, que es justo lo que se mira al llegar un
 * pedido o al dar de baja a un club.
 */
function getData(gpsDevices, clubesPorId) {
	let data = []
	for (let i = 0; i < gpsDevices.length; ++i) {
		const elem = {}
		elem.reference = gpsDevices[i].reference
		elem.club = gpsDevices[i].club ? clubesPorId.get(String(gpsDevices[i].club)) ?? '?' : null
		elem.code = gpsDevices[i].code
		elem.phone = gpsDevices[i].phone
		elem.status = gpsDevices[i].status
		elem.reasons = gpsDevices[i].reasons
		elem.connected = gpsDevices[i].connected
		elem.network = gpsDevices[i].network
		elem.networkQuota = gpsDevices[i].network // segunda columna, mismo objeto
		elem.ageSec = gpsDevices[i].ageSec
		elem.sats = gpsDevices[i].sats
		elem.lastTimestamp = gpsDevices[i].lastTimestamp
		elem.lastLoginAt = gpsDevices[i].lastLoginAt
		elem.batteryLevel = gpsDevices[i].batteryLevel
		elem.lastSpeedKmh = gpsDevices[i].lastSpeedKmh
		elem.isCharging = gpsDevices[i].isCharging
		elem.signalStrength = gpsDevices[i].signalStrength
		elem.createdAt = gpsDevices[i].createdAt
		elem.default = gpsDevices[i].default
		elem.data = gpsDevices[i]
		elem.id = gpsDevices[i]._id
		data = data.concat(elem)
	}
	return data
}

const POLL_MS = 12000

export default function GpsDevicesPage() {
	const [data, setData] = useState([])
	const [gpsDeviceId, setGpsDeviceId] = useState(null)
	const [selectedGpsDevice, setSelectedGpsDevice] = useState(null)
	const [openConfirmDialog, setOpenConfirmDialog] = useState(null)
	const [refresh, setRefresh] = useState(false)
	const [loading, setLoading] = useState(true)
	const [lastFetchAt, setLastFetchAt] = useState(null)
	const mounted = useRef(true)
	const [qrDevice, setQrDevice] = useState(null)
	const [smsDevice, setSmsDevice] = useState(null)
	const [copiedLink, setCopiedLink] = useState(false)
	const history = useHistory()

	function buttonFormatter(cell) {
		const gpsDevice = cell

		return (
			<>
				{/*
					Dónde está: abre la página de la pegatina, que enseña la
					última posición en un mapa que se refresca solo. Nace del
					15/08, con dos aparatos perdidos y Josep preguntando por
					cada uno. Solo si el aparato tiene código de pegatina — con
					el IMEI esa página no da posición a propósito (SAIL-44).
				*/}
				{gpsDevice?.publicCode && (
					<Tooltip title="Dónde está (página de la pegatina)">
						<Button
							style={buttonsStyle}
							size="small"
							onClick={() =>
								window.open(
									`https://live.sailradar.com/device/${gpsDevice.publicCode}`,
									'_blank',
									'noopener'
								)
							}
						>
							<RoomIcon />
						</Button>
					</Tooltip>
				)}
				<Tooltip title="Show QR">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() => setQrDevice(gpsDevice)}
					>
						<CropFreeIcon />
					</Button>
				</Tooltip>
				<Tooltip title="Comandos SMS (rescate)">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() => setSmsDevice(gpsDevice)}
					>
						<SmsIcon />
					</Button>
				</Tooltip>
				<Tooltip title="Edit">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() => history.push('/edit-gpsDevice/' + cell._id)}
					>
						<EditIcon />
					</Button>
				</Tooltip>
				<Tooltip title="Delete">
					<Button
						style={buttonsStyle}
						size="small"
						onClick={() => {
							setGpsDeviceId(cell._id)
							setOpenConfirmDialog(2)
						}}
					>
						<DeleteIcon />
					</Button>
				</Tooltip>
			</>
		)
	}

	function defaultFormatter(cell) {
		return cell ? <div className="default-label">default</div> : ''
	}

	/** En existencias no es un hueco: es un estado, y hay que verlo. */
	function clubFormatter(cell) {
		return cell ? cell : <span style={{ color: '#98a4b0' }}>existencias</span>
	}

	const columns = [
		{ dataField: 'status', text: 'Estado', sort: true, formatter: statusFormatter },
		{ dataField: 'reference', text: 'Ref', sort: true },
		{ dataField: 'club', text: 'Club', sort: true, formatter: clubFormatter },
		{ dataField: 'code', text: 'Code', sort: true },
		{ dataField: 'connected', text: 'Conectado', sort: true, formatter: connectedFormatter },
		{ dataField: 'network', text: 'Red móvil', sort: false, formatter: networkFormatter },
		{ dataField: 'networkQuota', text: 'Datos SIM', sort: false, formatter: dataQuotaFormatter },
		{
			dataField: 'ageSec',
			text: 'Últ. pos.',
			sort: true,
			formatter: ageFormatter,
		},
		{ dataField: 'batteryLevel', text: 'Bat. %', sort: true, formatter: val => (val != null ? `${val}%` : '—') },
		{
			dataField: 'signalStrength',
			text: 'Señal',
			sort: true,
			formatter: (val) => (val != null ? `${val}` : '—'),
		},
		{ dataField: 'phone', text: 'Phone', sort: true },
		/**
		 * Las acciones, al final como en el resto de listados.
		 *
		 * Para que quepan sin arrastrar a lo ancho se han quitado tres columnas de
		 * detalle —satélites, velocidad y si está cargando— y se han acortado las
		 * cabeceras, que son las que mandan el ancho. Esos tres datos no se
		 * pierden: siguen en la ficha del aparato y en diagnosticoAparato.js, que
		 * es donde se miran de verdad cuando uno falla. En un vistazo a la flota
		 * no dicen nada que no diga ya el semáforo.
		 */
		{
			dataField: 'data',
			text: '',
			formatter: buttonFormatter,
			align: 'end',
			style: { whiteSpace: 'nowrap' },
			headerStyle: () => ({ width: '190px' }),
		},
	]

	const [clubesPorId, setClubesPorId] = useState(new Map())
	const [abrirLote, setAbrirLote] = useState(false)
	const [abrirPegatinas, setAbrirPegatinas] = useState(false)
	// IMEI que estan intentando conectar sin estar en la lista blanca.
	const [desconocidos, setDesconocidos] = useState([])
	const [codigos, setCodigos] = useState('')

	useEffect(() => {
		getClubs()
			.then((res) => setClubesPorId(new Map(res.data.map((c) => [String(c._id), c.fullName]))))
			// Sin los nombres la tabla sale igual, solo que con la columna vacia:
			// no puede tumbar el semaforo, que es para lo que se abre esta pagina.
			.catch(() => {})
	}, [])

	const loadGpsDevices = useCallback((showSpinner = true) => {
		if (showSpinner)
			setLoading(true)
		getGpsDevicesStatus()
			.then((res) => {
				if (!mounted.current)
					return
				if (res.status === 200) {
					// /gpsDevice/status devuelve { entries, summary }
					setData(getData(res.data.entries ?? res.data, clubesPorId))
					// Los que llaman a la puerta y no estan dados de alta.
					setDesconocidos(res.data.desconocidos ?? [])
					setRefresh(false)
					setLastFetchAt(new Date())
				}
			})
			.catch((error) => {
				if (!mounted.current)
					return
				alertError({
					error: error,
					customMessage: 'Could not get gpsDevices.',
				})
			})
			.finally(() => {
				if (mounted.current && showSpinner)
					setLoading(false)
			})
	}, [clubesPorId])

	useEffect(() => {
		mounted.current = true
		return () => {
			mounted.current = false
		}
	}, [])

	// Carga inicial, tras borrar/acciones (refresh), y sondeo en vivo
	useEffect(() => {
		loadGpsDevices(true)

		const tick = () => {
			if (document.visibilityState !== 'visible')
				return
			loadGpsDevices(false)
		}

		const intervalId = setInterval(tick, POLL_MS)
		const onVis = () => {
			if (document.visibilityState === 'visible')
				loadGpsDevices(false)
		}
		document.addEventListener('visibilitychange', onVis)

		return () => {
			clearInterval(intervalId)
			document.removeEventListener('visibilitychange', onVis)
		}
	}, [refresh, loadGpsDevices])

	return (
		<>
			<Card>
				<CardHeader title="GpsDevices list">
					<CardHeaderToolbar>
						<small className="text-muted mr-3">
							{lastFetchAt
								? `Última actualización: ${lastFetchAt.toLocaleTimeString()} · auto cada ${POLL_MS / 1000}s`
								: ''}
							{loading ? ' · cargando…' : ''}
						</small>
						{/* Todos los aparatos en un mapa que se refresca solo: para
						    recogerlos después de una regata sin abrir nueve páginas
						    una a una. La página vive en el visor, como la de cada
						    aparato; pide sesión de gestión allí. */}
						<Tooltip title="Todos los aparatos en un mapa, con su última posición">
							<Button
								variant="outlined"
								size="small"
								style={{ marginRight: 8 }}
								startIcon={<RoomIcon />}
								onClick={() => window.open('https://live.sailradar.com/flota', '_blank', 'noopener')}
							>
								Mapa de la flota
							</Button>
						</Tooltip>
						<Button
							variant="outlined"
							size="small"
							style={{ marginRight: 8 }}
							onClick={() => setAbrirLote(true)}
						>
							Alta en lote
						</Button>
						{/* El paso siguiente al alta en lote: cuando llega una caja se
						    dan de alta los IMEI, se imprimen las pegatinas y se pegan.
						    Antes esto pedía una consola (scripts/codigosPegatina.js). */}
						<Tooltip title="Imprimir las pegatinas QR de los aparatos">
							<Button
								variant="outlined"
								size="small"
								style={{ marginRight: 8 }}
								startIcon={<LocalPrintshopIcon />}
								onClick={() => setAbrirPegatinas(true)}
							>
								Pegatinas
							</Button>
						</Tooltip>
						<Tooltip title="Refrescar ahora">
							<Button
								variant="outlined"
								size="small"
								style={{ marginRight: 8 }}
								disabled={loading}
								onClick={() => loadGpsDevices(true)}
							>
								<RefreshIcon />
							</Button>
						</Tooltip>
						<button
							type="button"
							className="btn btn-primary"
							onClick={() => history.push('/edit-gpsDevice')}
						>
							Add new
						</button>
					</CardHeaderToolbar>
				</CardHeader>
				<CardBody>
					{/*
					  QUIEN ESTA LLAMANDO A LA PUERTA.

					  Un aparato cuyo IMEI no esta dado de alta recibe un portazo del
					  servidor y se le cierra la conexion. Desde aqui eso se veia igual
					  que un aparato apagado o sin cobertura, y la unica prueba estaba
					  en el log del servidor. Casi siempre es el aparato que alguien
					  esta intentando poner en marcha en ese mismo momento.
					*/}
					{desconocidos.length > 0 && (
						<div className="alert alert-warning" style={{ fontSize: 13 }}>
							<b>
								{desconocidos.length === 1
									? 'Un aparato esta intentando conectar y no esta dado de alta.'
									: `${desconocidos.length} aparatos estan intentando conectar y no estan dados de alta.`}
							</b>{' '}
							El servidor les cierra la puerta, asi que no suben posiciones. Si son
							tuyos, dalos de alta con <b>Alta en lote</b> y volveran solos.
							<ul style={{ marginTop: 8, marginBottom: 8 }}>
								{desconocidos.map((d) => (
									<li key={d.imei}>
										<code>{d.imei}</code> · {d.veces} intento(s), el ultimo{' '}
										{formatLastTime(d.ultimaVez)}
									</li>
								))}
							</ul>
							<Button
								variant="outlined"
								size="small"
								onClick={() => {
									// Se abre el alta con los IMEI ya escritos: no hay que
									// copiarlos a mano de un aviso que esta ahi al lado.
									setCodigos(desconocidos.map((d) => d.imei).join('\n'))
									setAbrirLote(true)
								}}
							>
								Darlos de alta
							</Button>
						</div>
					)}
					<Table data={data} columns={columns} />
					<ConfirmDialog
						title={'Are you sure you want to remove this gpsDevice?'}
						open={openConfirmDialog === 2}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							deleteGpsDevice(gpsDeviceId)
								.then((res) => {
									if (res.status === 204) {
										alertSuccess({
											title: 'Deleted!',
											customMessage:
												'GpsDevice removed successfully.',
										})
										setRefresh(true)
									}
								})
								.catch((error) => {
									alertError({
										error: error,
										customMessage: 'Could not remove gpsDevice.',
									})
								})
						}}
					/>
					<ConfirmDialog
						title={`Are you sure you want to ${
							selectedGpsDevice?.active ? 'end' : 'start'
						} this gpsDevice?`}
						open={openConfirmDialog === 4}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							updateGpsDevice(selectedGpsDevice._id, {
								...selectedGpsDevice,
								active: !selectedGpsDevice?.active,
							})
								.then((res) => {
									if (res.status === 200) {
										alertSuccess({
											title: 'Success!',
											customMessage: !selectedGpsDevice?.active
												? 'GpsDevice Started'
												: 'GpsDevice Ended',
										})
										setRefresh(true)
									}
								})
								.catch((error) => {
									alertError({
										error: error,
										customMessage:
											'Could not update the gpsDevice.',
									})
								})
						}}
					></ConfirmDialog>
					<ConfirmDialog
						title="Dar de alta una caja de aparatos"
						open={abrirLote}
						setOpen={setAbrirLote}
						onConfirm={() => {
							altaEnLote(codigos)
								.then((res) => {
									// Los repetidos no son un fallo: se acusan y se saltan.
									const rep = res.data.repetidos?.length
										? ` ${res.data.repetidos.length} ya estaban y se han saltado.`
										: ''
									alertSuccess({
										title: 'Dados de alta',
										customMessage: `${res.data.creados} aparatos nuevos (${res.data.referencias.join(', ')}).${rep}`,
									})
									setCodigos('')
									loadGpsDevices(true)
								})
								.catch((error) => {
									alertError({ error, customMessage: 'No se pudieron dar de alta.' })
								})
						}}
					>
						<TextField
							label="Los IMEI, uno por linea"
							multiline
							minRows={6}
							fullWidth
							value={codigos}
							onChange={(e) => setCodigos(e.target.value)}
							placeholder={`861261021655523
861261027926852`}
						/>
						<p style={{ marginTop: 12, color: '#7b8794' }}>
							Entran sin club, a existencias, y se les pone referencia siguiendo la mayor que haya
							(GPS8, GPS9...). Los que ya esten en la base se saltan.
						</p>
					</ConfirmDialog>

				</CardBody>
			</Card>
			<Snackbar
				open={copiedLink}
				autoHideDuration={6000}
				onClose={() => setCopiedLink(false)}
			>
				<MuiAlert severity="info" sx={{ width: '100%' }}>
					GpsDevice link copied
				</MuiAlert>
			</Snackbar>
			<SmsCommandDialog
				device={smsDevice}
				open={!!smsDevice}
				onClose={() => setSmsDevice(null)}
			/>
			{/* En el orden de la tabla, que es el que tiene en la cabeza quien
			    está mirando la pantalla. Va `row.data` y no la fila: la fila es
			    lo que pinta cada columna y no lleva ni `_id` ni `publicCode`. */}
			<HojaPegatinasDialog
				open={abrirPegatinas}
				onClose={() => {
					setAbrirPegatinas(false)
					// Los códigos recién puestos han de verse en la tabla: si no,
					// el botón del QR de la fila seguiría diciendo «sin código».
					loadGpsDevices(false)
				}}
				aparatos={data.map((fila) => fila.data).filter(Boolean)}
			/>
			<Dialog open={!!qrDevice} onClose={() => setQrDevice(null)} maxWidth="xs" fullWidth>
				<DialogTitle>
					{qrDevice?.publicCode ?? 'sin código de pegatina'}
					<IconButton
						style={{ position: 'absolute', right: 8, top: 8 }}
						onClick={() => setQrDevice(null)}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent style={{ textAlign: 'center', padding: '20px' }}>
					{qrDevice && (
						<>
							{/*
						  EL QR LLEVA EL CÓDIGO DE PEGATINA, NO EL IMEI.
						  El IMEI va impreso en el propio aparato y en un pedido
						  viene casi correlativo: con uno se adivinaban los demás,
						  y esta pantalla enseña dónde está el barco. El código de
						  pegatina es aleatorio, así que tenerlo significa haber
						  tenido el aparato en la mano.
						  Se pone desde el boton "Pegatinas" de la cabecera.
						*/}
							{qrDevice.publicCode ? (
								<>
									<QRCodeCanvas value={`https://live.sailradar.com/device/${qrDevice.publicCode}`} size={256} />
									<div className="text-muted mt-3" style={{ fontSize: 12 }}>
									live.sailradar.com/device/{qrDevice.publicCode}
									</div>
									<div className="text-muted mt-2" style={{ fontSize: 11 }}>
									IMEI {qrDevice.code} — no va en la pegatina
									</div>
								</>
							) : (
								<div className="alert alert-warning" style={{ fontSize: 13 }}>
								Este aparato no tiene código de pegatina todavía. Se le pone desde el
								botón <b>Pegatinas</b> de la cabecera, que además prepara la hoja para
								imprimir. Sin él, la pantalla abre pero NO enseña el mapa.
								</div>
							)}
						</>
					)}
				</DialogContent>
			</Dialog>
		</>
	)
}
